// Imports => React
import { useMemo } from 'react';

// Imports => Constants
import { TYPES } from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
  AcIsNull,
  AcIsArray,
  AcIsObject,
  AcIsEmptyString,
  AcIsEmail,
} from '@opp/ui-kit';

export const useFormActions = ({
  t,
  fields = {},
  setFields = () => {},
  errors = {},
  setErrors = () => {},
}) => {
  const hasErrors = useMemo(() => {
    if (!AcIsSet(errors) || !AcIsObject(errors)) return false;
    let result = false;

    for (let key in errors) {
      if (!AcIsNull(errors[key])) {
        result = true;
        break;
      }
    }

    return result;
  }, [fields, errors]);

  const getChildData = (obj, key) => obj[key];

  const cloneDeepLimited = (obj) => JSON.parse(JSON.stringify(obj));

  const setFormValues = (data, name, value) => {
    return new Promise((resolve) => {
      const dataClone = cloneDeepLimited(data);
      const keys = name.split('.'); // 'a.b.c' => ['a', 'b', 'c'] | 'a' => ['a']
      const nestedSegments = keys.slice(0, -1); // ['a', 'b'] | []
      const [finalSegment] = keys.slice(-1); // 'c' | 'a'
      const finalData = nestedSegments.reduce(getChildData, dataClone);
      finalData[finalSegment] = value;
      resolve(dataClone);
      return dataClone;
    });
  };

  const handleInputChange = (event, name, value, type, checked) => {
    return new Promise((resolve) => {
      let _value = (event && event.target && event.target.value) || value;

      if (type === TYPES.CHECKBOX) {
        const arr = fields[name];
        if (arr && AcIsArray(arr)) {
          if (checked && arr.indexOf(_value) === -1) arr.push(_value);
          if (!checked && arr.indexOf(_value) > -1)
            arr.splice(arr.indexOf(_value), 1);
          _value = arr;
        }
      } else if (type === TYPES.BOOLEAN) {
        _value = checked;
      } else if (type === TYPES.ON_OFF) {
        _value = checked ? 1 : 0;
      }

      if (setFields) {
        setFormValues(fields, name, _value).then((values) => {
          setFields(values);
        });
      }

      resolve();
    });
  };

  const handleInputValidation = (name, value, required, type) => {
    let result = errors;

    switch (type) {
      case TYPES.EMAIL:
        if ((!AcIsSet(value) || AcIsEmptyString(value)) && required) {
          result[name] = t('validation.required', { ns: 'common' });
        } else if (!AcIsEmptyString(value) && !AcIsEmail(value)) {
          result[name] = t('validation.email', { ns: 'common' });
        } else {
          result[name] = null;
        }
        break;

      case TYPES.IBAN:
        if ((!AcIsSet(value) || AcIsEmptyString(value)) && required) {
          result[name] = t('validation.required', { ns: 'common' });
        } else {
          result[name] = null;
        }
        break;

      case TYPES.PASSWORD:
        if ((!AcIsSet(value) || AcIsEmptyString(value)) && required) {
          result[name] = t('validation.required', { ns: 'common' });
        } else {
          result[name] = null;
        }
        break;

      case TYPES.TEXT:
      case TYPES.TEXTAREA:
        if ((!AcIsSet(value) || AcIsEmptyString(value)) && required) {
          result[name] = t('validation.required', { ns: 'common' });
        } else {
          result[name] = null;
        }
        break;

      case TYPES.RATING:
        if ((!AcIsSet(value) || AcIsEmptyString(value)) && required) {
          result[name] = t('validation.required', { ns: 'common' });
        } else {
          result[name] = null;
        }
        break;

      case TYPES.BOOLEAN:
        if ((!AcIsSet(value) || AcIsEmptyString(value)) && required) {
          result[name] = t('validation.required', { ns: 'common' });
        } else {
          result[name] = null;
        }
        break;

      case TYPES.CHECKBOX:
        if (!AcIsSet(fields[name]) && required) {
          result[name] = t('validation.required', { ns: 'common' });
        } else if (
          AcIsArray(fields[name]) &&
          fields[name].length === 0 &&
          required
        ) {
          result[name] = t('validation.one-option-required', { ns: 'common' });
        } else {
          result[name] = null;
        }
        break;

      default:
    }

    if (setErrors) setErrors(result);

    return result[name];
  };

  return {
    hasErrors,
    handleInputChange,
    handleInputValidation,
  };
};
